/**
 * CDP SDK — recognising a site's signed-in readers with nothing done on the site
 * (NEWS-LOGIN-IDENTITY-2026-10-26 §5, decisions Р-ID1…Р-ID10).
 *
 * Driven by the Site Profile's `identity` block, which we write in CDP. Four sources, by strength:
 *  А  a page that shows the signed-in reader's e-mail (dir.bg `/profile`, 24chasa `/profil`) — verified;
 *     on 24chasa also read in the background once per browser for readers kept signed in (Р-ID10);
 *  Б  the site's "signed in" marker — says that someone is signed in, never who;
 *  В  the login form's e-mail at submit. A submit is not a sign-in, so the e-mail is held SERVER-side
 *     (services/auth-candidate.ts) and becomes an identify only when the next page shows Б. The browser
 *     keeps a 10-minute cookie with the salted hash, never the address: on dir.bg the sign-in happens on
 *     profile.dir.bg and lands on another origin, and the only storage the two share is a cookie that
 *     every third-party script on the page can read.
 *  (Г registration — not used on any site, Р-ID3.)
 *
 * Never read: a password field (the e-mail input is taken by its own selector and refused if it is
 * `type=password`), anything on a password page (the module does not start there; core drops every
 * event from one anyway — url-scrub.ts).
 */

import type { SiteIdentityConfig, SiteProfile } from './types';
import { isSensitivePath } from './url-scrub';
import { sha256, log } from './utils';

/** What the core gives the module — nothing else of its state is reachable from here. */
export interface AuthIdentityApi {
  profile(): SiteProfile;
  writeKey: string;
  cookieDomain?: string;
  getUserId(): string | null;
  hasPersonalization(): boolean;
  /** Runs `fn` once a TCF CMP has answered (at once without one, or when its wait is over) — so the consent in
   *  force is the reader's, not the implied default of the first moment (both news sites have a CMP). */
  whenConsentKnown(fn: () => void): void;
  /** An identify carrying `context.identity`; `setUser` also makes `userId` this browser's user. */
  sendIdentity(opts: {
    userId: string | null;
    setUser: boolean;
    traits: Record<string, unknown>;
    identity: Record<string, unknown>;
  }): void;
  track(event: string, properties: Record<string, unknown>): void;
  reset(): void;
}

const PENDING_COOKIE = 'cdp_auth';
const PENDING_MAX_AGE_S = 600;
/** This browser is already this reader (uid), set by a confirm or a verified page — a cookie on the site's cookie
 *  domain, not localStorage: dir.bg confirms on dnes.dir.bg and shows the e-mail on profile.dir.bg, two origins. */
const SEEN_COOKIE = 'cdp_auth_seen';
const SEEN_MAX_AGE_S = 24 * 60 * 60;
const FETCH_KEY = 'cdp_auth_fetch';
const FETCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const DEFAULT_WAIT_MS = 6000;
const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,63}$/;

/** A trimmed, lower-cased e-mail, or null. */
export function normalizeEmail(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const v = raw.trim().toLowerCase();
  return EMAIL_RE.test(v) ? v : null;
}

/**
 * The userId for an e-mail: a hash salted with the site's write key (Р-ID6). The e-mail itself goes only
 * in the identify's traits — the userId sits in a cookie for a year, readable by every script on the page,
 * and the salt keeps it from being matched against the plain SHA-256 lists ad tech trades in.
 */
export async function identityUid(writeKey: string, email: string): Promise<string> {
  return 'h:' + (await sha256('cdp1:' + writeKey + ':' + email));
}

/** Glob on the path, or on host + path when the pattern starts with a host; `*` matches every page. */
export function pageMatches(pattern: string, loc: { hostname: string; pathname: string }): boolean {
  if (!pattern) return false;
  if (pattern === '*') return true;
  const slash = pattern.indexOf('/');
  const hasHost = slash > 0 && pattern.slice(0, slash).includes('.');
  const subject = hasHost ? loc.hostname + loc.pathname : loc.pathname;
  const re = new RegExp('^' + pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*') + '/?$', 'i');
  return re.test(subject);
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]!) : null;
}

function writeCookie(name: string, value: string, maxAgeS: number, cookieDomain?: string): void {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  const domain = cookieDomain ? `; domain=${cookieDomain}` : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeS}; SameSite=Lax${domain}${secure}`;
}

function clearCookie(name: string, cookieDomain?: string): void {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  if (cookieDomain) document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax; domain=${cookieDomain}${secure}`;
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${secure}`;
}

const writePending = (uid: string, cookieDomain?: string) => writeCookie(PENDING_COOKIE, uid, PENDING_MAX_AGE_S, cookieDomain);
const clearPending = (cookieDomain?: string) => clearCookie(PENDING_COOKIE, cookieDomain);

function readJson<T>(key: string): T | null {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode — the dedup is a nicety */
  }
}

/** Resolves true as soon as `selector` matches, false after `waitMs`. */
function waitFor(selector: string, waitMs: number): Promise<boolean> {
  const has = () => {
    try {
      return !!document.querySelector(selector);
    } catch {
      return false;
    }
  };
  if (has()) return Promise.resolve(true);
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: boolean) => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(v);
    };
    const observer = new MutationObserver(() => {
      if (has()) finish(true);
    });
    observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'href'] });
    const timer = setTimeout(() => finish(has()), waitMs);
  });
}

function textOf(el: Element | null): string {
  if (!el) return '';
  if (el instanceof HTMLInputElement) return el.type === 'password' ? '' : el.value;
  return el.textContent || '';
}

/** Base64url → bits ('0'/'1' string), or null. */
function b64urlBits(seg: string): string | null {
  try {
    const b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '==='.slice((b64.length + 3) % 4));
    let bits = '';
    for (let i = 0; i < bin.length; i++) bits += bin.charCodeAt(i).toString(2).padStart(8, '0');
    return bits;
  } catch {
    return null;
  }
}

/**
 * The reader's TCF choice for purposes 5/6 (personalised content → `personalization`), as a CMP on another subdomain
 * of the site stored it: Google Funding Choices' `FCCDCF` (a JSON array; index 3 holds `[tcString, …]` once the
 * reader chose) or the IAB `euconsent-v2` cookie. null when there is no stored choice. For a page WITHOUT a CMP of its
 * own — profile.dir.bg, where the reader signs in, runs none, while dnes.dir.bg runs Google's and keeps the choice in
 * a `.dir.bg` cookie (27.10). In the TC string's core segment the purpose consents are bits 152–175.
 */
export function storedTcfPersonalization(cookieString: string): boolean | null {
  const get = (name: string): string | null => {
    const m = cookieString.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return m ? m[1]! : null;
  };
  let tc: string | null = get('euconsent-v2');
  if (!tc) {
    const fc = get('FCCDCF');
    if (fc) {
      try {
        const arr = JSON.parse(decodeURIComponent(fc)) as unknown[];
        const entry = Array.isArray(arr) ? arr[3] : null;
        tc = Array.isArray(entry) && typeof entry[0] === 'string' ? entry[0] : null;
      } catch {
        tc = null;
      }
    }
  }
  if (!tc) return null;
  const bits = b64urlBits(tc.split('.')[0]!);
  if (!bits || bits.length < 176 || parseInt(bits.slice(0, 6), 2) !== 2) return null;
  return bits[152 + 4] === '1' || bits[152 + 5] === '1';
}

/**
 * Start the module for this page. Returns a cleanup. No-op without an enabled `identity` block, and on a
 * password page.
 */
export function startAuthIdentity(api: AuthIdentityApi): () => void {
  const profile = api.profile();
  const cfg: SiteIdentityConfig | undefined = profile.identity;
  if (!cfg || cfg.enabled !== true) return () => {};
  const loc = { hostname: location.hostname, pathname: location.pathname };
  if (isSensitivePath(loc.pathname, loc.hostname, profile.privacy?.excludePaths)) return () => {};

  const observe = cfg.mode === 'observe';
  const waitMs = Math.min(Math.max(Number(cfg.loggedIn?.waitMs) || DEFAULT_WAIT_MS, 0), 15000);
  const cleanups: Array<() => void> = [];
  const formsHere = (cfg.forms || []).filter((f) => f && f.emailField && pageMatches(f.match, loc));
  const onFormPage = formsHere.length > 0;
  const uidCache = new Map<string, string>();
  const consentKnown = () => new Promise<void>((resolve) => api.whenConsentKnown(resolve));
  // Personalisation as the SDK knows it — and, on a page without a CMP of its own, as the reader chose it on the
  // site's CMP elsewhere (27.10: a reader who refused on dnes.dir.bg still sent a candidate from profile.dir.bg).
  const personalized = (): boolean => {
    if (!api.hasPersonalization()) return false;
    const cmpHere = typeof (window as unknown as { __tcfapi?: unknown }).__tcfapi === 'function';
    return cmpHere || profile.consent?.tcf === false || storedTcfPersonalization(document.cookie) !== false;
  };
  // The funnel, without a person in it (analytics consent): where a sign-in stops — 27.10, three candidates and no
  // confirmation said nothing about why. `Auth Detected {source, stage, outcome}`; no e-mail, no uid.
  const funnel = (source: string, stage: string, outcome: string) => api.track('Auth Detected', { source, stage, outcome });

  const uidFor = async (email: string): Promise<string> => {
    const hit = uidCache.get(email);
    if (hit) return hit;
    const uid = await identityUid(api.writeKey, email);
    uidCache.set(email, uid);
    return uid;
  };

  const loggedIn = (): Promise<boolean> =>
    cfg.loggedIn?.selector ? waitFor(cfg.loggedIn.selector, waitMs) : Promise.resolve(false);

  /** A verified e-mail (А): identify, unless this browser already is this reader (24 h). */
  const identifyVerified = async (email: string, method: 'profile_page' | 'profile_fetch'): Promise<void> => {
    await consentKnown();
    if (!personalized()) {
      funnel(method, 'verified', 'no_personalization');
      return;
    }
    const uid = await uidFor(email);
    const current = api.getUserId();
    if (current === uid && readCookie(SEEN_COOKIE) === uid) return;
    // Another reader signed in on this browser — start them on a fresh anonymous id, or the two
    // would merge (the anonymous id is a strong identifier).
    if (current && current !== uid) api.reset();
    funnel(method, 'verified', observe ? 'observe' : 'sent');
    if (!observe) api.sendIdentity({ userId: uid, setUser: true, traits: { email, auth_method: method }, identity: { stage: 'verified', method } });
    writeCookie(SEEN_COOKIE, uid, SEEN_MAX_AGE_S, api.cookieDomain);
    log('Auth identity: verified (' + method + ')');
  };

  // ── Б + В: the sign-in this browser started on a previous page ──
  const pending = readCookie(PENDING_COOKIE);
  let confirming: Promise<void> = Promise.resolve();
  if (pending && /^h:[0-9a-f]{64}$/.test(pending)) {
    if (onFormPage) {
      // Back on the login form: the attempt failed (or was abandoned).
      clearPending(api.cookieDomain);
      api.whenConsentKnown(() => {
        funnel('login_form', 'discard', 'back_on_form');
        if (!observe && personalized()) api.sendIdentity({ userId: null, setUser: false, traits: {}, identity: { stage: 'discard' } });
      });
    } else {
      confirming = loggedIn().then(async (yes) => {
        await consentKnown();
        if (!yes) {
          funnel('login_form', 'confirm', 'no_marker'); // not this page — the cookie lives on for the next one (10 min)
          return;
        }
        clearPending(api.cookieDomain);
        if (!personalized()) {
          funnel('login_form', 'confirm', 'no_personalization');
          return;
        }
        funnel('login_form', 'confirm', observe ? 'observe' : 'sent');
        if (!observe) api.sendIdentity({ userId: pending, setUser: true, traits: {}, identity: { stage: 'confirm', uid: pending } });
        writeCookie(SEEN_COOKIE, pending, SEEN_MAX_AGE_S, api.cookieDomain);
        log('Auth identity: sign-in confirmed');
      });
    }
  }

  // ── В: the login form on this page ──
  if (onFormPage) {
    const emailOf = (form: HTMLFormElement): string | null => {
      for (const f of formsHere) {
        let input: Element | null = null;
        try {
          input = form.querySelector(f.emailField) || document.querySelector(f.emailField);
        } catch {
          input = null;
        }
        if (!input || !form.contains(input) || !(input instanceof HTMLInputElement) || input.type === 'password') continue;
        return normalizeEmail(input.value);
      }
      return null;
    };
    // The hash is ready before the submit — the page may navigate away right after it.
    const onField = (ev: Event) => {
      const t = ev.target;
      if (!(t instanceof HTMLInputElement) || t.type === 'password' || !t.form) return;
      const email = emailOf(t.form);
      if (email) void uidFor(email);
    };
    const onSubmit = (ev: Event) => {
      const form = ev.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (cfg.logout?.selector && safeMatches(form, cfg.logout.selector)) return;
      const email = emailOf(form);
      if (!email) return;
      const send = (uid: string) => {
        if (!personalized()) {
          funnel('login_form', 'candidate', 'no_personalization');
          return;
        }
        const current = api.getUserId();
        if (current && current !== uid) api.reset();
        writePending(uid, api.cookieDomain);
        funnel('login_form', 'candidate', observe ? 'observe' : 'sent');
        if (!observe) api.sendIdentity({ userId: null, setUser: false, traits: { email }, identity: { stage: 'candidate', uid, method: 'login_form' } });
        log('Auth identity: sign-in candidate held');
      };
      // At the login form the CMP has long answered (it asks on the first page), so this runs at once.
      api.whenConsentKnown(() => {
        const cached = uidCache.get(email);
        if (cached) send(cached);
        else void uidFor(email).then(send).catch(() => {});
      });
    };
    document.addEventListener('change', onField, true);
    document.addEventListener('focusout', onField, true);
    document.addEventListener('submit', onSubmit, true);
    cleanups.push(() => {
      document.removeEventListener('change', onField, true);
      document.removeEventListener('focusout', onField, true);
      document.removeEventListener('submit', onSubmit, true);
    });
  }

  // ── Sign-out ──
  if (cfg.logout?.selector) {
    const sel = cfg.logout.selector;
    const out = () => {
      clearPending(api.cookieDomain);
      clearCookie(SEEN_COOKIE, api.cookieDomain);
      if (api.getUserId()) api.reset();
      log('Auth identity: signed out');
    };
    const onClick = (ev: Event) => {
      const t = ev.target as Element | null;
      if (t && typeof t.closest === 'function' && safeClosest(t, sel)) out();
    };
    const onSubmitOut = (ev: Event) => {
      const t = ev.target;
      if (t instanceof HTMLFormElement && safeMatches(t, sel)) out();
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmitOut, true);
    cleanups.push(() => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('submit', onSubmitOut, true);
    });
  }

  // ── А: the reader's e-mail on this page, or (Р-ID10) on the site's profile page read once ──
  void confirming.then(async () => {
    for (const entry of cfg.pageData || []) {
      if (!entry || !entry.email || !pageMatches(entry.match, loc)) continue;
      let el: Element | null = null;
      try {
        el = document.querySelector(entry.email);
      } catch {
        el = null;
      }
      const email = normalizeEmail(textOf(el));
      if (email) {
        await identifyVerified(email, 'profile_page');
        return;
      }
    }
    const fetchEntry = (cfg.pageData || []).find((e) => e && e.email && typeof e.fetchUrl === 'string');
    if (!fetchEntry || api.getUserId() || readCookie(PENDING_COOKIE)) return;
    const last = readJson<{ at: number }>(FETCH_KEY);
    if (last && Date.now() - last.at < FETCH_TTL_MS) return;
    if (!(await loggedIn())) return;
    await consentKnown();
    if (!personalized() || api.getUserId()) return;
    let url: URL;
    try {
      url = new URL(fetchEntry.fetchUrl!, location.href);
    } catch {
      return;
    }
    if (url.origin !== location.origin) return; // never another origin's page
    writeJson(FETCH_KEY, { at: Date.now() }); // once, whatever the answer
    try {
      const res = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!res.ok || new URL(res.url || url.href).pathname !== url.pathname) return; // e.g. sent to /login
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const email = normalizeEmail(textOf(doc.querySelector(fetchEntry.email)));
      funnel('profile_fetch', 'fetch', email ? 'found' : 'not_found');
      if (email) await identifyVerified(email, 'profile_fetch');
    } catch {
      /* the next try is in 30 days */
    }
  });

  return () => cleanups.forEach((fn) => fn());
}

function safeMatches(el: Element, selector: string): boolean {
  try {
    return el.matches(selector);
  } catch {
    return false;
  }
}

function safeClosest(el: Element, selector: string): boolean {
  try {
    return !!el.closest(selector);
  } catch {
    return false;
  }
}
